import React, { useState, useContext } from "react";
import { UserContext } from "../context/UserContext"; // Import UserContext
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ImageIcon from "@mui/icons-material/Image";
import StarIcon from "@mui/icons-material/Star";
import LinkIcon from '@mui/icons-material/Link';
import axios from "axios";
import API_BASE_URL from "../../src/config";

const PostOffer = () => {
  const { user } = useContext(UserContext); // Access user from context
  const [offer, setOffer] = useState({
    title: "",
    business: "",
    category: "",
    location: "",
    original_price: "",
    discount_price: "",
    description: "",
    expiry_date: "",
    business_website_url:"",
    image_url: null,
    feature: false, // default is false
    user_id: user ? user.id : null, // Include user_id from context (default to null if no user)
  });
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setOffer({
      ...offer,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    });
  };

  const categories = [
    "Home & Living",
    "Business Support",
    "Travel & Logistics",
    "Event & Party Services",
    "Education & Learning",
    "Digital & Tech Services",
    "Special Services",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSuccessMessage("");
    setErrorMessage("");
  
    // Ensure user_id is taken from the context
    if (!user || !user.id) {
      setErrorMessage("❌ User not logged in.");
      setSubmitted(false);
      return;
    }
  
    const offerWithUserId = {
      ...offer,
      user_id: user.id,  // Ensure user_id is included
    };
  
    try {
      const response = await axios.post(`${API_BASE_URL}/offers`, offerWithUserId, {
        // const response = await axios.post("http://localhost:5000/api/offers",offerWithUserId,{
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      setSuccessMessage(`✅ Offer Posted: ${response.data.title} by ${response.data.business}`);
      setOffer({
        title: "",
        business: "",
        category: "",
        location: "",
        original_price: "",
        discount_price: "",
        description: "",
        expiry_date: "",
        business_website_url:"",
        image_url: null,
        feature: false,
      });
    } catch (error) {
      console.error("Error posting offer:", error);
      setErrorMessage("❌ Failed to post offer. Please try again.");
    }
    setSubmitted(false);
  };
  


  return (
    <Container maxWidth="sm" sx={{ mt: 5,mb:4 }}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h5"
            color = "#42326e" 
            fontWeight="bold"
            textAlign="center"
            mb={3}
          >
            🎉 Post an Offer
          </Typography>

          {successMessage && (
            <Snackbar
              open
              autoHideDuration={4000}
              onClose={() => setSuccessMessage("")}
            >
              <Alert severity="success">{successMessage}</Alert>
            </Snackbar>
          )}
          {errorMessage && (
            <Snackbar
              open
              autoHideDuration={4000}
              onClose={() => setErrorMessage("")}
            >
              <Alert severity="error">{errorMessage}</Alert>
            </Snackbar>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LocalOfferIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Offer Title"
                name="title"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <StorefrontIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Business Name"
                name="business"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CategoryIcon sx={{ color:"#42326e" }} />
              <TextField
                select
                fullWidth
                label="Category"
                name="category"
                onChange={handleChange}
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LocationOnIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Location"
                name="location"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Original Price (₹)"
                name="original_price"
                type="number"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon color="secondary" />
              <TextField
                fullWidth
                label="Discount Price (₹)"
                name="discount_price"
                type="number"
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CalendarTodayIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Expiry Date"
                name="expiry_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <DescriptionIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Offer Description"
                name="description"
                multiline
                rows={3}
                required
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LinkIcon sx={{ color:"#42326e" }} />
              <TextField
                fullWidth
                label="Business Website URL"
                name="business_website_url"
                required
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <ImageIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Image URL"
                variant="outlined"
                name="image_url"
                value={offer.image_url}
                onChange={handleChange}
                required
              />
            </Box>

            {/* Image Preview */}
            {offer.image_url && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <img
                  src={offer.image_url}
                  alt="Offer Preview"
                  style={{
                    maxWidth: "100%",
                    height: "150px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  name="feature"
                  checked={offer.feature}
                  onChange={handleChange}
                />
              }
              label={
                <>
                  <StarIcon color="secondary" /> Feature as Premium Offer
                </>
              }
            />

            <Button
              variant="contained"
              color="success"
              fullWidth
              type="submit"
              disabled={submitted}
              sx={{
                borderRadius: 2,
                mt: 2,
                backgroundColor: "#42326e",
                color: "white",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {submitted ? "Submitting..." : "Post Offer"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PostOffer;
